/**
 * ImageGalleryUploader Component
 * Multi-image upload with preview (max 6 images)
 * Supports drag & drop, reordering, and caption editing
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { FiUpload, FiAlertCircle } from 'react-icons/fi';
import SortableImageItem from './SortableImageItem';
import {
  IMAGE_LIMITS,
  IMAGE_ERROR_MESSAGES,
  canUploadMoreImages,
  getRemainingImageSlots
} from '../../constants/limits';

// Gallery image file type for uploader
interface GalleryImageFile {
  id: string;
  file: File;
  preview: string;
  caption?: string;
  altText?: string;
}

interface ImageGalleryUploaderProps {
  maxImages?: number;
  existingImages?: Array<{
    id: string;
    imageUrl: string;
    caption?: string;
    altText?: string;
    displayOrder: number;
  }>;
  onChange: (images: GalleryImageFile[]) => void;
  disabled?: boolean;
}

const ImageGalleryUploader: React.FC<ImageGalleryUploaderProps> = ({
  maxImages = IMAGE_LIMITS.GALLERY_MAX, // Use constant as default
  existingImages = [],
  onChange,
  disabled = false
}) => {
  const [images, setImages] = useState<GalleryImageFile[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Calculate remaining slots
  const remainingSlots = getRemainingImageSlots(images.length);
  const canUploadMore = canUploadMoreImages(images.length);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [images]);

  // Drag & drop handler
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError(IMAGE_ERROR_MESSAGES.FILE_TOO_LARGE());
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError(IMAGE_ERROR_MESSAGES.INVALID_FORMAT);
      }
      return;
    }

    // Check if upload limit reached
    const currentRemainingSlots = getRemainingImageSlots(images.length);

    if (currentRemainingSlots === 0) {
      setError(IMAGE_ERROR_MESSAGES.GALLERY_NO_SLOTS());
      return;
    }

    if (acceptedFiles.length > currentRemainingSlots) {
      setError(`สามารถอัปโหลดได้อีกเพียง ${currentRemainingSlots} รูป`);
      acceptedFiles = acceptedFiles.slice(0, currentRemainingSlots);
    }

    const newImages = acceptedFiles.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      caption: '',
      altText: ''
    }));

    const updated = [...images, ...newImages];
    setImages(updated);
    onChange(updated);
  }, [images, maxImages, onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    disabled: disabled || images.length >= maxImages,
    multiple: true
  });

  // Remove image
  const handleRemove = (id: string) => {
    const imageToRemove = images.find(img => img.id === id);
    if (imageToRemove?.preview) {
      URL.revokeObjectURL(imageToRemove.preview);
    }

    const updated = images.filter(img => img.id !== id);
    setImages(updated);
    onChange(updated);
    setError(null);
  };

  // Update caption/alt text
  const handleUpdateMetadata = (id: string, field: 'caption' | 'altText', value: string) => {
    const updated = images.map(img =>
      img.id === id ? { ...img, [field]: value } : img
    );
    setImages(updated);
    onChange(updated);
  };

  // Handle drag end (reorder)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex(img => img.id === active.id);
      const newIndex = images.findIndex(img => img.id === over.id);

      const reordered = arrayMove(images, oldIndex, newIndex);
      setImages(reordered);
      onChange(reordered);
    }
  };

  return (
    <div className="space-y-4">
      {/* Image Counter and Status */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">
            รูปภาพแกลเลอรี่:
          </span>
          <span className={`text-lg font-bold ${images.length >= maxImages ? 'text-red-600' : 'text-blue-600'}`}>
            {images.length}/{maxImages}
          </span>
        </div>
        {images.length >= maxImages ? (
          <span className="text-xs font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
            เต็มแล้ว
          </span>
        ) : (
          <span className="text-xs font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
            เหลืออีก {remainingSlots} รูป
          </span>
        )}
      </div>

      {/* Warning when limit reached */}
      {images.length >= maxImages && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
          <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">คุณอัปโหลดรูปครบ {maxImages} รูปแล้ว</span>
            <br />
            กรุณาลบรูปเก่าก่อนเพิ่มรูปใหม่
          </p>
        </div>
      )}

      {/* Upload Zone */}
      {canUploadMore && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${disabled || !canUploadMore ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <input {...getInputProps()} />
          <FiUpload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-700 font-medium mb-2">
            {isDragActive ? 'วางรูปภาพที่นี่...' : 'คลิกเพื่อเลือกรูปภาพ หรือลากรูปภาพมาวาง'}
          </p>
          <p className="text-sm text-gray-500">
            รองรับ JPG, PNG, WebP ขนาดไม่เกิน 5MB
          </p>
          <p className="text-xs text-gray-400 mt-1">
            สามารถอัปโหลดได้อีก {remainingSlots} รูป
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg p-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Image Grid with Drag & Drop */}
      {images.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images.map(img => img.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image, index) => (
                <SortableImageItem
                  key={image.id}
                  id={image.id}
                  image={image}
                  index={index}
                  isEditing={editingId === image.id}
                  onRemove={handleRemove}
                  onEdit={() => setEditingId(image.id)}
                  onSaveMetadata={handleUpdateMetadata}
                  onCancelEdit={() => setEditingId(null)}
                  disabled={disabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Instructions */}
      {images.length > 0 && (
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3">
          <p className="font-medium mb-1">คำแนะนำ:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>ลากรูปภาพเพื่อจัดลำดับการแสดงผล</li>
            <li>คลิกไอคอนแก้ไขเพื่อเพิ่มคำอธิบายภาพ</li>
            <li>รูปแรกจะแสดงเป็นรูปหลักในหน้ารายละเอียด</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ImageGalleryUploader;
export type { GalleryImageFile };
